import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Button, Chip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import type { TagSchedule } from '~/services/TagsService';
import { formatDateSlashes } from '~/services/TimeService';

import { useGetTagSchedules } from '../hooks';

interface TagSchedulesListProps {
  tagId?: string;
  schedules: Array<TagSchedule>;
  onSchedulesChange: (schedules: Array<TagSchedule>) => void;
  onEditClick: (schedule: TagSchedule | TagSchedule) => void;
  onCreateClick: () => void;
}

export default function TagSchedulesList({
  tagId,
  schedules = [],
  onSchedulesChange,
  onEditClick,
  onCreateClick,
}: TagSchedulesListProps) {
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 25,
    page: 0,
  });

  const {
    data = undefined,
    isLoading = false,
    error = null,
  } = useGetTagSchedules(
    tagId
      ? {
          tagId,
          offset: paginationModel.page * paginationModel.pageSize,
          limit: paginationModel.pageSize,
        }
      : undefined,
  );
  const handleDeleteClick = (schedule: TagSchedule) => {
    if (!tagId) {
      const updatedSchedules = schedules.filter((s) => s.id !== schedule.id);
      onSchedulesChange(updatedSchedules);
      return;
    }
  };

  const columns: Array<GridColDef<TagSchedule>> = [
    {
      field: 'brands',
      headerName: 'Brands',
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams<TagSchedule>) => (
        <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" height="100%">
          {params.row.brands.map((brand) => (
            <Chip key={brand} label={brand} size="small" />
          ))}
        </Box>
      ),
    },
    {
      field: 'activeStartDate',
      headerName: 'Active Period Start',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<TagSchedule>) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.activeStartDate ? formatDateSlashes(params.row.activeStartDate) : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'activeEndDate',
      headerName: 'Active Period End',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<TagSchedule>) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.activeEndDate ? formatDateSlashes(params.row.activeEndDate) : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 130,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEditClick(params.row)}
          color="inherit"
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteClick(params.row)}
          color="error"
        />,
      ],
    },
  ];

  const allSchedules = tagId
    ? data?.result || []
    : schedules.map((schedule) => ({
        ...schedule,
        tagId: '',
      }));

  return (
    <Box>
      {!!error && <Alert severity="error">{error?.message}</Alert>}
      <Box mb={4} display="flex" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateClick}>
          Add Schedule
        </Button>
      </Box>
      <DataGrid
        loading={isLoading && !!tagId}
        rows={allSchedules}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row: TagSchedule) =>
          `${row?.id}-${row?.tagId}-${row?.brands?.join(',')}-${row?.activeStartDate}-${row?.activeEndDate}`
        }
        paginationMode="server"
        rowCount={tagId ? data?.pagination?.total_items : allSchedules.length}
        slots={{
          pagination: GridPagination,
        }}
        autoHeight
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    </Box>
  );
}
